/**
 * `/cli/device?code=XXXX-XXXX` — デバイスコードでのログイン（gh auth login と同じ形）の画面（pages/CliDevice.tsx）の「React に依存しない部分」。
 * 2026-10-07 console/device-login（docs/teams/console/tasks/device-login.md）。node --test で確かめる（cliDevice.test.ts）。
 *
 * WHY 作ったか: クラウドの Claude Code から `kumodeck login` すると CLI は利用者の PC に無いので、/cli/authorize の 127.0.0.1 の戻り先に
 *   繋がらない。0.1.16 では「繋がらない画面のアドレスを貼る」にしたが、founder「普通の人知らないでしょ」。利用者の手順を
 *   「リンクを開いて、チャット（かターミナル）と同じ文字なら Allow」だけにする。
 *
 * 往復（約束 = 3 人共通の API の形。サーバーは Core device login・CLI は Creator device login が作る。勝手に変えない）:
 *   1. CLI: POST /v1/developers/cli-device/start { device } → { deviceCode, userCode, verificationUrl, expiresIn: 600, interval: 5 }
 *      → チャット / ターミナルに userCode と verificationUrl（= この画面 `/cli/device?code=<userCode>`）を出す
 *   2. この画面（ログイン必須）: GET /v1/developers/cli-device/:userCode → { userCode, device, createdAt, expiresAt, status }／無ければ 404
 *   3. Allow → POST …/:userCode/approve・Cancel → POST …/:userCode/deny（200 { ok: true }・pending 以外なら 409）
 *   4. CLI: POST /v1/developers/cli-device/token { deviceCode } を待ちながら聞き、許可済みなら資格情報を受け取る（画面は関わらない）
 *
 * WHY URL の code を厳しく読むか: 他人が作ったリンクでもよい値。形が違えば読みに行かず「このリンクは使えません」（サーバーへの当て推量の
 *   問い合わせも減る。サーバーにも回数の制限がある）。
 * WHY 画面に userCode を大きく出すか: 守りの芯は「本人が自分のチャットに出た文字と見比べる」こと。他人が自分の CLI で始めたコードの
 *   リンクを送りつけても、本人のチャットには同じ文字が無い = 押さない、が成り立つ。
 * device（端末の名前）は CLI の申告（偽れる）= 表示だけ。
 */

/** 画面のパス（サーバーの verificationUrl・packages/cli と同じ。変えるなら全部） */
export const CLI_DEVICE_PATH = '/cli/device';

/**
 * userCode の形: `XXXX-XXXX`。紛らわしい文字（0 O 1 I L）を除いた大文字と数字（約束）。
 * WHY 0 O 1 I L を除くか: 人が目で見比べる / 手で打つ文字なので、見間違えやすい組を最初から作らない（サーバーも作らない）
 */
export const USER_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
const USER_CODE_RE = /^[ABCDEFGHJKMNPQRSTUVWXYZ2-9]{4}-[ABCDEFGHJKMNPQRSTUVWXYZ2-9]{4}$/;

/**
 * URL のクエリ（location.search）→ userCode。形が違えば null（画面は「このリンクは使えません」）。
 * 小文字・前後の空白・ハイフン抜け（8 文字）はそろえる: 人が手で打ち直したリンクでも通す（意味は同じ文字）
 */
export function readUserCode(search: string): string | null {
  const raw = (new URLSearchParams(search).get('code') ?? '').trim().toUpperCase();
  const code = /^[A-Z0-9]{8}$/.test(raw) ? `${raw.slice(0, 4)}-${raw.slice(4)}` : raw;
  return USER_CODE_RE.test(code) ? code : null;
}

export type CliDeviceStatus = 'pending' | 'approved' | 'denied' | 'expired' | 'used';

/** GET /v1/developers/cli-device/:userCode の応答（約束の形） */
export interface CliDeviceResponse {
  userCode: string;
  device: string;
  createdAt: string;
  expiresAt: string;
  status: CliDeviceStatus;
}

/**
 * 画面の出し分け:
 *   ask      = 確かめて Allow / Cancel
 *   notFound = 無いコード（404・打ち間違い・別の環境のリンク）
 *   expired  = 期限切れ（10 分）
 *   used     = もう答えた（許可済み・断った・CLI が受け取った）。一回限り
 */
export type CliDeviceView = 'ask' | 'notFound' | 'expired' | 'used';

/**
 * 読んだ応答（か、そのエラーの status）→ 出し分け。now は画面を描いた時刻（テストで固定する）。
 * WHY 期限を手元でも見るか: サーバーの status は読んだ時点の物。pending でも expiresAt を過ぎていれば押しても 409 になるだけなので、
 *   先に「期限切れ」を出す（押してから断られるより迷わない）
 */
export function deviceView(res: { data: CliDeviceResponse | null; errorStatus: number | null }, now: number = Date.now()): CliDeviceView {
  if (res.errorStatus === 404 || res.errorStatus === 410) return 'notFound';
  const d = res.data;
  if (!d) return 'notFound';
  if (d.status === 'expired') return 'expired';
  if (d.status !== 'pending') return 'used';
  const exp = Date.parse(d.expiresAt);
  if (Number.isFinite(exp) && exp <= now) return 'expired';
  return 'ask';
}

/** サーバーの応答の端末の名前: 制御文字を落として 64 文字まで（cliAuthorize.ts の cleanDevice と同じ規則）。空なら null */
export { cleanDevice } from './cliAuthorize.ts';
